import {
  getProvisioningStatus,
  provision,
  requestMenuImport,
  triggerMenuUpdate,
  updatePlatformOpenStatus,
} from 'api'
import { PartnerResource, ProvisioningStatusResponse } from 'api/models'
import { AxiosError } from 'axios'
import { Section, Table, TableMobile, Toast } from 'components'
import BaseButton from 'components/Button/BaseButton'
import ChilipiperSection from 'components/ChilipiperSection'
import SectionHeader from 'components/Section/SectionHeader'
import SectionTitle from 'components/Section/SectionTitle'
import { useIsMobile, useOpenModal } from 'hooks'
import { useAuth0 } from 'libs/auth0-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  menuPlatformsState,
  onboardingStatusWebHookCall,
  uberPublishSuccessfulWebHookCall,
} from 'state/atoms'
import { PlatformName, PlatformSummary } from 'types'
import { showError } from 'utils'

import { ActivatePlatformComponent } from './ActivatePlatformComponent'
import { getMobilePlatformColumns, getPlatformColumns } from './columns'
import { DoordashFeedbackSection } from './doordashFeedbackSection'
import PlatformCloseReopenModal from './PlatformCloseReopenModal'

type Props = {
  partner: PartnerResource
  locationPlatforms: PlatformSummary[] | undefined
  isLoading: boolean
  refetchLocationPartners: () => Promise<void>
}

const ignoreStatus = ['unknown', 'pending', 'removed', 'requested', 'confirmed', 'menu_qualified']

const Platforms = ({ partner, locationPlatforms, isLoading, refetchLocationPartners }: Props) => {
  const { user } = useAuth0()
  const isAdminUser = user?.email?.includes('@chowlyinc.com')
  const { isMobile } = useIsMobile()

  const doorDashonboardingStatusWebHook = useRecoilValue(onboardingStatusWebHookCall)
  const UberPublishSuccessfulWebHookResponse = useRecoilValue(uberPublishSuccessfulWebHookCall)

  const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformSummary[]>([])
  const [menuPlatforms, setMenuPlatforms] = useRecoilState(menuPlatformsState)
  const [isCloseReopenModalOpen, setIsCloseReopenModalOpen] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [doordashStatus, setDoordashStatus] = useState<string>()
  const [doordashExclusionCode, setDoordashExclusionCode] = useState('')
  const [uberEatsStatus, setUberEatsStatus] = useState<boolean>()
  const [uberMenuPublish, setUberMenuPublish] = useState(false)
  const [isUberPlatformOnboardingStatus, setisUberPlatformOnboardingStatus] = useState(false)
  const [requestedMenuImport, setRequestedMenuImport] = useState(false)
  const [successfulDoordashActivateRequest, setSuccessfulDoordashActivateRequest] = useState(false)
  const [successfulUberEatsActivateRequest, setSuccessfulUberEatsActivateRequest] = useState(false)

  const timezone = partner?.attributes.timezone ?? 'UTC'

  const uberEatsNeedsActivationCheck =
    !successfulUberEatsActivateRequest &&
    uberMenuPublish &&
    isUberPlatformOnboardingStatus &&
    !uberEatsStatus

  useEffect(() => {
    setMenuPlatforms(locationPlatforms)
  }, [locationPlatforms])

  useEffect(() => {
    const doordashPlatform = menuPlatforms?.filter(
      (platform) => platform.internalName === PlatformName.DOORDASH,
    )
    const uberEatsPlatform = menuPlatforms?.filter(
      (platform) => platform.internalName === PlatformName.UBEREATS,
    )
    const uberMenuPublishStatus =
      uberEatsPlatform && uberEatsPlatform.length > 0
        ? uberEatsPlatform[0].menuPublishStatus
          ? uberEatsPlatform[0].menuPublishStatus?.attributes.success
          : null
        : null
    if (uberMenuPublishStatus && uberEatsPlatform && uberEatsPlatform[0]?.status === 'Onboarding') {
      setUberEatsStatus(uberEatsPlatform[0]?.provisioned)
      setUberMenuPublish(uberMenuPublishStatus)
      setisUberPlatformOnboardingStatus(true)
    }

    if (doordashPlatform?.length && doordashPlatform?.[0]?.aasmState === 'onboarding') {
      const fetchData = async () => {
        let provisionStatusResponse: ProvisioningStatusResponse | null = null

        try {
          provisionStatusResponse = await getProvisioningStatus(doordashPlatform[0].id)
        } catch (err) {
          console.log('Error getting provisioning status: ', (err as AxiosError).message)
        }

        if (provisionStatusResponse) {
          const { data: provisioningStatusData } = provisionStatusResponse
          setDoordashStatus(provisioningStatusData?.attributes.aasm_state)
          setDoordashExclusionCode(
            provisioningStatusData?.attributes?.error_details?.exclusion_code,
          )
        }
      }

      fetchData()
    }
  }, [menuPlatforms, doorDashonboardingStatusWebHook, UberPublishSuccessfulWebHookResponse])

  const showDoordashFeedbackSection =
    isAdminUser && doordashStatus && !ignoreStatus.includes(doordashStatus)

  const showPlatformActivation =
    isAdminUser && (doordashStatus === 'menu_qualified' || uberEatsNeedsActivationCheck)

  const isPauseAllEnabled = (locationPlatforms ?? [])?.some(
    ({ availability, pausable }) => pausable && availability === 'Not Paused',
  )

  const doordashNeedsActivationCheck =
    doordashStatus === 'menu_qualified' && !successfulDoordashActivateRequest

  const handleGoToCOO = useCallback((url: string) => {
    if (url) {
      window.open(url, '_blank')
    }
  }, [])

  const handlePause = useCallback(
    (row?: PlatformSummary) => {
      if (locationPlatforms?.length) {
        setSelectedPlatforms(
          row
            ? [row]
            : locationPlatforms.filter(
                ({ availability, pausable }) => pausable && availability === 'Not Paused',
              ),
        )
        setIsCloseReopenModalOpen(true)
      }
    },
    [locationPlatforms],
  )

  const handleActivateDoordash = async () => {
    const doordashPlatform = menuPlatforms?.filter(
      (platform) => platform.internalName === 'Doordash',
    )
    if (!doordashPlatform) return
    else if (doordashPlatform && !doordashPlatform[0].id) return
    else {
      try {
        await provision(Number(doordashPlatform[0].id))
        Toast.success({ message: 'Requested Doordash activation!' })
        setSuccessfulDoordashActivateRequest(true)
      } catch (err) {
        Toast.error({
          message:
            'Failed in requesting Doordash activation. Try again. For additional help please contact Chowly Support using the “C” icon in the lower right corner of your screen.',
        })
      }
    }
  }

  const handleActivateUberEats = async () => {
    const uberEatsPlatform = menuPlatforms?.filter(
      (platform) => platform.internalName === PlatformName.UBEREATS,
    )
    if (!uberEatsPlatform) return
    else if (uberEatsPlatform && !uberEatsPlatform[0].id) return
    else {
      try {
        await provision(Number(uberEatsPlatform[0].id))
        Toast.success({ message: 'Requested Uber Eats activation!' })
        setSuccessfulUberEatsActivateRequest(true)
        setisUberPlatformOnboardingStatus(false)
      } catch (err) {
        Toast.error({
          message:
            'Failed in requesting Uber Eats activation. Try again. For additional help please contact Chowly Support using the “C” icon in the lower right corner of your screen.',
        })
      }
    }
  }

  const openModal = useOpenModal()
  const openManageAccount = () => openModal('AddPlatform')

  const reSyncDoordash = async () => {
    if (partner.id) {
      try {
        if (doordashStatus) {
          if (
            !(partner?.attributes?.pos === 'Epson') &&
            !(partner?.attributes?.pos === 'ChowlySdk')
          ) {
            await requestMenuImport(partner.id, false, false)
          }
          const doordashPlatform = menuPlatforms?.filter(
            (platform) => platform.internalName === 'Doordash',
          )
          if (doordashPlatform && doordashPlatform[0].id) {
            await triggerMenuUpdate(Number(doordashPlatform[0].id))
          }
        }
        Toast.success({ message: 'Menu Sync requested successfully' })
        setRequestedMenuImport(true)
      } catch (err) {
        Toast.error({
          message:
            'Failed in saving the changes. Try again. For additional help please contact Chowly Support using the “C” icon in the lower right corner of your screen.',
        })
      }
    }
  }

  const handleCloseReopen = async (temporary_closure?: string) => {
    setIsCloseReopenModalOpen(false)
    setIsUpdating(true)
    await Promise.all([
      selectedPlatforms.map(async ({ id, availability }) => {
        try {
          await updatePlatformOpenStatus(id, {
            // if its currently paused reopen, else close
            open_availability: availability === 'Paused' ? true : false,
            ...(availability !== 'Paused' && {
              temporary_closure,
            }),
          })
          refetchLocationPartners()
        } catch (e) {
          showError(e)
        }
      }),
    ])
    setIsUpdating(false)
  }

  const platformsColumns = useMemo(() => {
    if (!partner.id) {
      return []
    }
    return getPlatformColumns(
      handlePause,
      refetchLocationPartners,
      handleGoToCOO,
      partner.id,
      isAdminUser,
    )
  }, [handlePause, isPauseAllEnabled])

  const mobilePlatformsColumns = useMemo(() => {
    if (!partner.id) {
      return []
    }
    return getMobilePlatformColumns(
      handlePause,
      refetchLocationPartners,
      handleGoToCOO,
      partner.id,
      isAdminUser,
    )
  }, [handlePause, isPauseAllEnabled])

  const TableComponent = isMobile ? TableMobile : Table

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Platforms</SectionTitle>
        <div style={{ display: 'flex', columnGap: '0.75rem' }}>
          {isAdminUser && (
            <BaseButton onClick={() => openManageAccount()} variant='solid' disabled={isLoading}>
              Add Platform
            </BaseButton>
          )}
          <BaseButton size='small' disabled={isLoading} onClick={() => handlePause()}>
            Pause All
          </BaseButton>
        </div>
      </SectionHeader>
      <TableComponent
        path={'./styles.ts'}
        columns={isMobile ? mobilePlatformsColumns : platformsColumns}
        rowKey='id'
        data={menuPlatforms ?? []}
        isLoading={isLoading}
      />
      {/* {(isLoading || isUpdating) && <SkeletonLoader />} */}
      {!(isLoading || isUpdating) && (
        <ChilipiperSection
          message='Interested in adding additional platforms to your Chowly account?'
          actionBtnText='Get in touch to learn more'
        />
      )}
      {/* Doordash error section */}
      {showDoordashFeedbackSection && (
        <DoordashFeedbackSection
          errorType={doordashStatus}
          doordashExclusionCode={doordashExclusionCode}
          requestedMenuImport={requestedMenuImport}
          reSyncDoordash={reSyncDoordash}
        />
      )}
      {showPlatformActivation && (
        <ActivatePlatformComponent
          handleActivateDoordash={handleActivateDoordash}
          uberEatsNeedsActivationCheck={uberEatsNeedsActivationCheck}
          doordashNeedsActivationCheck={doordashNeedsActivationCheck}
          handleActivateUberEats={handleActivateUberEats}
        />
      )}
      <PlatformCloseReopenModal
        isOpen={isCloseReopenModalOpen}
        platforms={selectedPlatforms}
        timezone={timezone}
        onClose={() => setIsCloseReopenModalOpen(false)}
        onSubmit={handleCloseReopen}
      />
    </Section>
  )
}

export default Platforms
